<template>
  <v-row>
    <!-- En-tête et actions -->
    <v-col cols="6" class="text-left">
      <v-btn
        x-small
        elevation="10"
        class="text-xs font-weight-semibold"
        color="error darken-2"
        @click="cancelSelection"
        :disabled="selectedUsers.length === 0"
        :loading="isUpdating2"
      >
        <v-icon x-small left>mdi-cancel</v-icon>
        Cancel ({{ selectedUsers.length }})
      </v-btn>
    </v-col>
    
    <v-col cols="6" class="text-right">
      <v-btn
        x-small
        elevation="10"
        class="text-xs font-weight-semibold"
        color="success darken-2"
        @click="validateSelectedPayments"
        :disabled="selectedUsers.length === 0"
        :loading="isUpdating"
      >
        <v-icon x-small left>mdi-check-all</v-icon>
        To validate ({{ selectedUsers.length }})
      </v-btn>
    </v-col>

    <!-- Contenu principal -->
    <v-col cols="12">
      <v-card>
        <!-- Filtres et recherche -->
        <v-row class="pt-4 mx-2 pt-0">
          <v-col cols="12" sm="6" class="px-4 pt-2 mt-1 mb-0 pb-0">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Rechercher un compte..."
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tableau -->
        <v-card-title class="pt-0 d-flex align-end mt-auto" style="min-height: 80px">
        <span class="me-3">Pending accounts({{ businessUsers.length }})</span>
        <v-spacer></v-spacer>
      </v-card-title>

        <v-data-table
          v-model="selectedUsers"
          :headers="headers"
          :items="businessUsers"
          :loading="isLoading"
          :search="search"
          show-select
          item-key="id"
          class="table-rounded"
          :items-per-page="10"
        >
          <!-- Colonne Statut Paiement -->
          <template #[`item.payment_status`]="{ item }">
            <v-chip 
              :color="item.payment_status ? 'success' : 'primary'" 
              dark 
              small
              @click.stop="openStatusDialog(item)"
            >
              {{ item.payment_status ? 'Validé' : 'En attente' }}
              <v-icon right x-small>{{ item.payment_status ? 'mdi-check' : 'mdi-clock' }}</v-icon>
            </v-chip>
          </template>

          <!-- Colonne Balance -->
          <template #[`item.balance`]="{ item }">
            <span class="font-weight-bold">{{ formatCurrency(item.balance) }}</span>
          </template>

          <!-- Colonne Date Création -->
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <!-- Message vide -->
          <template #no-data>
            <v-alert type="info" class="mx-4 my-2">
              No business account pending validation
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Dialog de confirmation -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 white--text primary">
          Confirm validation
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to validate {{ selectedUsers.length }} selected account(s)?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="success" @click="confirmValidation">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog d'annulation -->
    <v-dialog v-model="cancelDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 white--text error">
          Confirm cancellation
        </v-card-title>
        <v-card-text class="pt-4">
          You are about to cancel {{ selectedUsers.length }} business account(s).
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmCancellation">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      isLoading: false,
      isUpdating: false,
      isUpdating2: false,
      confirmDialog: false,
      cancelDialog: false,
      search: '',
      selectedUsers: [],
      businessUsers: [],
      headers: [
        { text: 'User', value: 'username' },
        { text: 'Email', value: 'email' },
        { text: 'Balance', value: 'balance', align: 'center' },
        { text: 'Payment Status', value: 'payment_status', align: 'center' },
        { text: 'Creation Date', value: 'created_at', align: 'center' },
        { text: 'Phone', value: 'transaction_phone', align: 'center' }
      ]
    }
  },

  mounted() {
    this.fetchBusinessUsers()
  },

  methods: {
    async fetchBusinessUsers() {
      this.isLoading = true
      try {
        const response = await axios.get('/api/v1/manager/business-accounts/pending/')
        this.businessUsers = response.data.map(user => ({
          ...user,
          created_at: new Date(user.created_at)
        }))
      } catch (error) {
        this.showError('Erreur de chargement des comptes')
      } finally {
        this.isLoading = false
      }
    },
    cancelSelection() {
      this.cancelDialog = true
    },
    async confirmCancellation() {
      this.cancelDialog = false
      this.isUpdating2 = true
      
      try {
        const cancellations = this.selectedUsers.map(user => 
          axios.put(`/api/v1/manager/cancel-profiles/${user.id}/cancel-business-account/`)
        )

        const results = await Promise.allSettled(cancellations)
        
        // Gérer les résultats
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            this.showSuccess(`${this.selectedUsers[index].username} cancelled`)
          } else {
            this.showError(`${this.selectedUsers[index].username} failed`)
          }
        })
        
        // Rafraîchir les données
        await this.fetchBusinessUsers()
        this.selectedUsers = []
        
      } catch (error) {
        this.showError('Error during cancellation process')
      } finally {
        this.isUpdating2 = false
      }
    },


    async validateSingleUser(user) {
      try {
        await axios.put(`/api/v1/manager/user-profiles/${user.id}/validate-payment/`, {
          payment_status: true
        })
        user.payment_status = true
        this.showSuccess(`Paiement de ${user.username} validé`)
      } catch (error) {
        this.showError(`Échec pour ${user.username}`)
      }
    },

    validateSelectedPayments() {
      this.confirmDialog = true
    },

    async confirmValidation() {
      this.confirmDialog = false
      this.isUpdating = true
      
      try {
        const validations = this.selectedUsers.map(user => 
          this.validateSingleUser(user)
        )

        await Promise.allSettled(validations)
        
        // Rafraîchir les données après traitement
        await this.fetchBusinessUsers()
        this.selectedUsers = []
        
        this.showSuccess('Traitement des validations terminé')
      } catch (error) {
        this.showError('Erreur lors du traitement global')
      } finally {
        this.isUpdating = false
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('fr-FR', { 
        style: 'currency', 
        currency: 'XAF' 
      }).format(value)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    showError(message) {
      this.$store.commit('setSnackbar', {
        message,
        color: 'error',
        show: true
      })
    },

    showSuccess(message) {
      this.$store.commit('setSnackbar', {
        message,
        color: 'success',
        show: true
      })
    }
  }
}
</script>



<style scoped>
.table-rounded {
  border-radius: 8px;
  overflow: hidden;
}

.v-card-title {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.v-data-table >>> .v-chip {
  cursor: pointer;
  transition: all 0.3s ease;
}

.v-data-table >>> .v-chip:hover {
  opacity: 0.9;
  transform: scale(1.05);
}
</style>